import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";

export interface BreakPayload {
  type: "duration" | "timeRange";
  durationHours?: number;
  startTime?: string;
  endTime?: string;
}

export interface TimeConfigPayload {
  type: "default" | "recurring";
  startTime: string;
  endTime: string;
  totalHours: number;
  recurringenabled?: boolean;
  recurrenceType?: "day" | "weekly" | "biweekly" | "monthly";
  interval?: number;
  selectedDays?: string[];
  date?: string;
  monthlyPattern?: string;
}

export interface WorkingHoursPayload {
  timeConfigs: TimeConfigPayload[];
  break: BreakPayload;
}

export interface OvertimeConfigPayload {
  allowed: boolean;
  maxHoursPerDay?: number;
  maxHoursPerMonth?: number;
  requiresApproval?: boolean;
}

export interface LateArrivalConfigPayload {
  graceMinutes?: number;
  markHalfDayAfterMinutes?: number;
  deductionEnabled?: boolean;
  deductMinutesFor?: number;
  forEveryMinutes?: number;
}

export interface EarlyDepartureConfigPayload {
  graceMinutes?: number;
  markHalfDayBeforeMinutes?: number;
}

export interface LocationPayload {
  isLocationCheck: boolean;
  lng: string;
  lat: string;
}

export interface RecurringConfigPayload {
  enabled: boolean;
  recurrenceType: "day" | "weekly" | "biweekly" | "monthly";
  interval: number;
  selectedDays: string[];
  selectedDate?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

export interface OrgUpdatePayload {
  name?: string;
  handbook?: string;
  members?: string[];
  projects?: string[];
  workingHours?: WorkingHoursPayload;
  overtimeConfig?: OvertimeConfigPayload;
  lateArrivalConfig?: LateArrivalConfigPayload;
  earlyDepartureConfig?: EarlyDepartureConfigPayload;
  location?: {
    lng: string;
    lat: string;
  };
  recurringConfig?: RecurringConfigPayload;
  expireAt?: string;
  logo?: string;
  isLocationCheck?: boolean;
  timezone?: string;
}

export interface Organization {
  _id: string;
  name: string;
  handbook?: string;
  members?: string[];
  projects?: string[];
  workingHours?: WorkingHoursPayload;
  overtimeConfig?: OvertimeConfigPayload;
  lateArrivalConfig?: LateArrivalConfigPayload;
  earlyDepartureConfig?: EarlyDepartureConfigPayload;
  location?: LocationPayload;
  recurringConfig?: RecurringConfigPayload;
  expireAt?: string;
  createdAt?: string;
  updatedAt?: string;
  logo?: string;
  isLocationCheck?: boolean;
  timezone?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

const updateOrgSettings = async (args: {
  orgId: string;
  payload: OrgUpdatePayload;
}): Promise<ApiResponse<Organization>> => {
  const { data } = await customAxios.patch<ApiResponse<Organization>>(
    `/organizations/${args.orgId}`,
    args.payload
  );
  return data;
};

export const useUpdateOrgSettings = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<Organization>,
    AxiosError,
    { orgId: string; payload: OrgUpdatePayload }
  >(updateOrgSettings, {
    onSuccess: async (res, variables) => {
      showNotification({
        title: "Organization updated",
        message: res.message || "Settings updated successfully",
        color: "green",
      });

      await queryClient.invalidateQueries([
        "organizationSettings",
        variables.orgId,
      ]);
      await queryClient.invalidateQueries(["organization", variables.orgId]);
      await queryClient.invalidateQueries(["org", variables.orgId]);
    },
    onError: (error) => {
      showNotification({
        title: "Update failed",
        message: "Something went wrong",
        color: "red",
      });
    },
  });
};
